import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api } from "./utils/api";

type SaveStatus = "idle" | "saving" | "saved" | "error";

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image"],
  ],
};

const mobileModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "indent",
  "link",
  "image",
];

const REFRESH_INTERVAL = 30_000;

export function RealTimeNotes({ isMobile }: { isMobile?: boolean } = {}) {
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastChangedAt = useRef<number>(0);

  useEffect(() => {
    const fetchNote = async (silent: boolean) => {
      if (!silent) setIsLoading(true);
      try {
        const response = await api.get<{ content: string }>("/realtime-notes");
        setNote(response.data.content ?? "");
      } catch (error) {
        console.error((error as Error)?.message ?? error);
        if (!silent) setSaveStatus("error");
      } finally {
        if (!silent) setIsLoading(false);
      }
    };

    fetchNote(false);

    refreshTimer.current = setInterval(() => {
      if (Date.now() - lastChangedAt.current >= REFRESH_INTERVAL) {
        fetchNote(true);
      }
    }, REFRESH_INTERVAL);

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, []);

  const saveNote = async (content: string) => {
    setSaveStatus("saving");
    try {
      await api.put("/realtime-notes", { content });
      setSaveStatus("saved");
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      console.error(error instanceof Error ? error.message : error);
      setSaveStatus("error");
    }
  };

  const handleChange = (value: string) => {
    setNote(value);
    lastChangedAt.current = Date.now();
    setSaveStatus("idle");
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveNote(value);
    }, 1500);
  };

  if (isLoading) {
    return <div className="p-4">Loading…</div>;
  }

  const statusText: Record<SaveStatus, string> = {
    idle: "",
    saving: "Saving…",
    saved: "Saved",
    error: "Error saving",
  };

  const statusColor: Record<SaveStatus, string> = {
    idle: "text-transparent",
    saving: "text-gray-400",
    saved: "text-green-500",
    error: "text-red-500",
  };

  return (
    <div className="h-full w-full p-4 pt-2 flex flex-col overflow-hidden">
      {isMobile && (
        <Link to="/mobile" className="btn btn-default mb-3 self-start">
          To Menu
        </Link>
      )}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-base font-semibold">Real-time notes</h1>
        <span className={`text-sm ${statusColor[saveStatus]}`}>
          {statusText[saveStatus] || "."}
        </span>
      </div>

      <div className="flex-1 min-h-0 mb-10">
        <ReactQuill
          className="h-full"
          theme="snow"
          value={note}
          onChange={handleChange}
          modules={isMobile ? mobileModules : modules}
          formats={formats}
        />
      </div>
    </div>
  );
}
